import React, { useRef } from 'react';
import classNames from 'classnames';
import { useInView } from 'react-cool-inview';
import {
  RiH1,
  RiH2,
  RiH3,
  RiH4,
  RiH5,
  RiH6,
  RiListOrdered,
  RiListUnordered,
  RiSeparator,
  RiTextWrap,
  RiFormatClear,
  RiFontColor,
  RiBlurOffLine,
  RiPaintFill,
  RiFontFamily,
} from 'react-icons/ri';

import type { Editor } from '../types';

import { setLink } from '../functions';
import { tipTapPluginsMode, fonts } from '../constants';

import { DropDownFont } from './DropDownFont';

import './EditorToolbar.scss';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    emojiReplacer: {
      insertEmoji: (emoji: string) => ReturnType;
    };
  }
}
type EditorToolbarPropsType = {
  editor: Editor;
  pluginsMode: tipTapPluginsMode;
};

const basePlugins = [
  'headings',
  'lists',
  'bold',
  'italic',
  'underline',
  'code',
  'link',
  'hardBreak',
  'horizontalRule',
  'unsetAllMarks',
  'colorFamily',
  'alignment',
];

const modeSets = {
  [tipTapPluginsMode.all]: [...basePlugins, 'image'],
  [tipTapPluginsMode.emailTemplates]: [...basePlugins],
  [tipTapPluginsMode.text]: ['headings', 'lists', 'bold', 'italic', 'underline', 'link', 'hardBreak', 'unsetAllMarks'],
  [tipTapPluginsMode.lists]: ['lists', 'bold', 'italic'],
};

function EditorToolbar({ editor, pluginsMode }: EditorToolbarPropsType) {
  const { observe, inView } = useInView({
    rootMargin: '-1px 0px 0px 0px',
    threshold: [1],
  });

  const modeSet = modeSets[pluginsMode];

  const colorPickerRef = useRef(null);
  const colorPickerBgRef = useRef(null);
  const fileInputRef = useRef(null);

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Image = reader.result;
        editor
          .chain()
          .focus()
          .setImage({
            src: base64Image as string,
          })
          .run();
      };
      reader.readAsDataURL(file);
    }
  };

  const openColorPickerTextColor = () => {
    if (colorPickerRef.current) {
      colorPickerRef.current.click();
    }
  };

  const openColorPickerBackgroundColor = () => {
    if (colorPickerBgRef.current) {
      colorPickerBgRef.current.click();
    }
  };

  const openFileDialog = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={classNames('ToolbarContainer', { sticky: !inView })} ref={observe}>
      <div className="Toolbar">
        <div className="toolbar-item spaced" onClick={() => editor.chain().focus().undo().run()}>
          <i className="fa fa-undo" />
        </div>
        <div className="toolbar-item spaced" onClick={() => editor.chain().focus().redo().run()}>
          <i className="fa fa-repeat" />
        </div>
        <div className="divider"></div>

        {modeSet.includes('headings') && (
          <>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 1 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            >
              <RiH1 className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 2 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            >
              <RiH2 className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 3 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            >
              <RiH3 className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 4 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 4 }).run()}
            >
              <RiH4 className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 5 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 5 }).run()}
            >
              <RiH5 className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('heading', { level: 6 }) })}
              onClick={() => editor.chain().focus().toggleHeading({ level: 6 }).run()}
            >
              <RiH6 className="icon" />
            </div>
            <div className="divider"></div>
          </>
        )}

        {modeSet.includes('lists') && (
          <>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('bulletList') })}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
            >
              <RiListUnordered className="icon" />
            </div>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('orderedList') })}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
            >
              <RiListOrdered className="icon" size={10} width={10} height={'10px'} />
            </div>
            <div className="divider"></div>
          </>
        )}

        {modeSet.includes('bold') && (
          <div
            className={classNames('toolbar-item spaced', { active: editor.isActive('bold') })}
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <i className="fa fa-bold" />
          </div>
        )}

        {modeSet.includes('italic') && (
          <div
            className={classNames('toolbar-item spaced', { active: editor.isActive('italic') })}
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <i className="fa fa-italic" />
          </div>
        )}

        {modeSet.includes('underline') && (
          <div
            className={classNames('toolbar-item spaced', { active: editor.isActive('underline') })}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <i className="fa fa-underline" />
          </div>
        )}

        {modeSet.includes('code') && (
          <div
            className={classNames('toolbar-item spaced', { active: editor.isActive('code') })}
            onClick={() => editor.chain().focus().toggleCode().run()}
          >
            <i className="fa fa-code" />
          </div>
        )}

        {modeSet.includes('link') && (
          <>
            <div
              className={classNames('toolbar-item spaced', { active: editor.isActive('link') })}
              onClick={() => setLink(editor)}
            >
              <i className="fa fa-external-link" />
            </div>
            <div className="divider"></div>
          </>
        )}

        {modeSet.includes('hardBreak') && (
          <div className="toolbar-item spaced" onClick={() => editor.chain().focus().setHardBreak().run()}>
            <RiTextWrap className="icon" />
          </div>
        )}

        {modeSet.includes('horizontalRule') && (
          <div className="toolbar-item spaced" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
            <RiSeparator className="icon" />
          </div>
        )}

        {modeSet.includes('unsetAllMarks') && (
          <>
            <div
              className="toolbar-item spaced"
              onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
            >
              <RiFormatClear className="icon" />
            </div>
            <div className="divider"></div>
          </>
        )}

        {modeSet.includes('colorFamily') && (
          <>
            <div
              className={classNames('toolbar-item spaced', { active: editor.getAttributes('textStyle').fontFamily })}
            >
              <DropDownFont icon={<RiFontFamily className="icon" />} fonts={fonts} editor={editor} />
            </div>
            <div className="color-picker spaced">
              <div className="toolbar-item spaced" onClick={openColorPickerTextColor}>
                <RiFontColor
                  className="icon"
                  style={{ color: editor.getAttributes('textStyle').color || 'rgb(122, 142, 146)' }}
                />
              </div>
              <input
                ref={colorPickerRef}
                type="color"
                onInput={(event: React.FormEvent<HTMLInputElement>) =>
                  editor.chain().focus().setColor(event.currentTarget.value).run()
                }
                value={editor.getAttributes('textStyle').color || '#000000'}
                data-testid="setColor"
              />
            </div>
            <div className="color-picker spaced">
              <div className="toolbar-item spaced" onClick={openColorPickerBackgroundColor}>
                <RiPaintFill
                  className="icon"
                  style={{ color: editor.getAttributes('textStyle').backgroundColor || 'rgb(122, 142, 146)' }}
                />
              </div>
              <input
                ref={colorPickerBgRef}
                type="color"
                onInput={(event: React.FormEvent<HTMLInputElement>) =>
                  editor.chain().focus().setBackgroundColor(event.currentTarget.value).run()
                }
                value={editor.getAttributes('textStyle').backgroundColor || '#ffffff'}
                data-testid="setColor"
              />
            </div>
            <div
              className="toolbar-item spaced"
              onClick={() => {
                editor.chain().focus().unsetColor().run();
                editor.chain().focus().unsetBackgroundColor().run();
              }}
              data-testid="unsetColor"
            >
              <RiBlurOffLine className="icon" />
            </div>
            <div className="divider"></div>
          </>
        )}

        {modeSet.includes('alignment') && (
          <>
            <div className="toolbar-item spaced" onClick={() => editor.chain().focus().setTextAlign('left').run()}>
              <i className="fa fa-align-left" />
            </div>
            <div className="toolbar-item spaced" onClick={() => editor.chain().focus().setTextAlign('center').run()}>
              <i className="fa fa-align-center" />
            </div>
            <div className="toolbar-item spaced" onClick={() => editor.chain().focus().setTextAlign('right').run()}>
              <i className="fa fa-align-right" />
            </div>
          </>
        )}

        {modeSet.includes('image') && (
          <>
            <div>
              <div className="toolbar-item spaced" onClick={openFileDialog}>
                <i className="fa fa-picture-o" />
              </div>
              <input
                ref={fileInputRef}
                className="image-loader"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export { EditorToolbar };
